import { useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import {
  cancelAdminOrder,
  confirmAdminOrder,
  type AdminCallAttemptOutcome,
  type AdminCancellationReason,
} from "@/lib/admin-api-client";
import { describeAdminError } from "./adminErrors";

const CANCELLATION_REASONS: Array<{ value: AdminCancellationReason; label: string }> = [
  { value: "CUSTOMER_REQUEST", label: "Demande du client" },
  { value: "UNREACHABLE", label: "Client injoignable" },
  { value: "OUT_OF_STOCK", label: "Rupture de stock" },
  { value: "FRAUD_SUSPECT", label: "Suspicion de fraude" },
  { value: "DELIVERY_FAILED", label: "Échec de livraison" },
];

/**
 * Actions de la file de confirmation téléphonique sur la fiche commande
 * (`/admin/commandes/:id`), affichées uniquement quand `status ===
 * "PENDING_CONFIRMATION"` (le parent ne monte ce composant que dans ce cas).
 * Porté depuis apps/web/components/admin/AdminOrderConfirmationActions.tsx :
 * `confirmOrderAction`/`cancelOrderAction` (Server Actions) remplacées par
 * `useMutation` direct sur `admin-api-client.ts` ; `router.refresh()` →
 * `invalidateQueries` (commande + liste).
 */
export function AdminOrderConfirmationActions({ orderId }: { orderId: string }) {
  const queryClient = useQueryClient();

  const [showCancelForm, setShowCancelForm] = useState(false);
  const [reason, setReason] = useState<AdminCancellationReason>("CUSTOMER_REQUEST");
  const [cancelNote, setCancelNote] = useState("");

  function invalidateOrder() {
    queryClient.invalidateQueries({ queryKey: ["admin", "order", orderId] });
    queryClient.invalidateQueries({ queryKey: ["admin", "orders"] });
  }

  const confirmMutation = useMutation({
    mutationFn: (outcome: AdminCallAttemptOutcome) => confirmAdminOrder(undefined, orderId, outcome),
    onSuccess: invalidateOrder,
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelAdminOrder(undefined, orderId, reason, cancelNote.trim() || undefined),
    onSuccess: () => {
      setShowCancelForm(false);
      setCancelNote("");
      invalidateOrder();
    },
  });

  const isPending = confirmMutation.isPending || cancelMutation.isPending;
  const error = confirmMutation.error ?? cancelMutation.error;

  function handleCancel(event: FormEvent) {
    event.preventDefault();
    cancelMutation.mutate();
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" disabled={isPending} onClick={() => confirmMutation.mutate("CONFIRMED")}>
          Confirmer
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={isPending}
          onClick={() => confirmMutation.mutate("UNREACHABLE")}
        >
          Injoignable
        </Button>
        <button
          type="button"
          disabled={isPending}
          onClick={() => setShowCancelForm((v) => !v)}
          className="rounded-sm border border-red-700 px-4 py-2 text-xs font-medium uppercase tracking-wide text-red-700 hover:bg-red-700 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          Annuler
        </button>
      </div>

      {showCancelForm ? (
        <form onSubmit={handleCancel} className="flex flex-col gap-2 rounded-sm border border-line bg-cream2 p-3">
          <label htmlFor="admin-order-cancel-reason" className="text-xs font-medium uppercase tracking-wide text-navy">
            Motif d&apos;annulation
          </label>
          <select
            id="admin-order-cancel-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value as AdminCancellationReason)}
            className="rounded-sm border border-line bg-white px-3 py-2 text-sm text-ink focus:border-gold focus:outline-none"
          >
            {CANCELLATION_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>

          <label htmlFor="admin-order-cancel-note" className="text-xs font-medium uppercase tracking-wide text-navy">
            Note (facultative)
          </label>
          <textarea
            id="admin-order-cancel-note"
            rows={2}
            maxLength={500}
            value={cancelNote}
            onChange={(event) => setCancelNote(event.target.value)}
            className="rounded-sm border border-line bg-white px-3 py-2 text-sm text-ink focus:border-gold focus:outline-none"
          />

          <div>
            <Button
              type="submit"
              size="sm"
              disabled={isPending}
              className="border-red-700 bg-red-700 text-white hover:border-red-800 hover:bg-red-800"
            >
              {cancelMutation.isPending ? "Annulation…" : "Confirmer l'annulation"}
            </Button>
          </div>
        </form>
      ) : null}

      {error ? (
        <p role="alert" className="text-sm text-red-700">
          {describeAdminError(error)}
        </p>
      ) : null}
    </div>
  );
}
